import { useEffect, useState } from "react";
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Button,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";
import "./LoginForm.css";
import { useReturnAfterSignIn } from "hooks/use-return-after-sign-in";
import { apiAxios } from "utils/axios";
import { useDispatch } from "react-redux";
import {
  setIsUserLoggedIn,
  setUserEmail,
  setUserId,
  setUserNickname,
  setUserRegDate,
  AuthType,
} from "redux/slices/userDataSlice";
import { clearGoogleIdentity } from "utils/auth/clearAuthSession";
import { storeUserApiKey } from "utils/auth/apiKeyStore";
import { trackUserLogin } from "../../../../utils/analytics";
import { requestOtp, verifyOtp } from "api/OtpAuth";
import { isEmailShape } from "utils/validation/emailValidation";
import { REF_STORAGE_KEY } from "components/Profile/ProfileReferals/helpers";

const RESEND_COOLDOWN_SECONDS = 60;

type Step = "email" | "code";

// Passwordless OTP sign-in: email -> code -> in. New and returning users take
// the same path (a new email is auto-registered on first correct code). Google
// + Telegram social login live alongside this in <AuthOptions /> (LoginView).
const LoginForm = () => {
  const returnAfterSignIn = useReturnAfterSignIn();
  const toast = useToast();
  const dispatch = useDispatch();
  const intl = useIntl();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Resend-after-cooldown timer.
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const validateEmail = (value: string): string | null => {
    if (!value) return "Email is required";
    if (!isEmailShape(value)) return "Invalid email address";
    return null;
  };

  // Fetch the freshly-signed-in user's profile (same shape the old login used).
  const getUserProfile = async (apiKey: string) => {
    const response = await apiAxios.get("/get_account_details", {
      headers: { Authorization: `Bearer ${apiKey}` },
    });
    if (response.data?.data?.user_data) {
      return response.data.data.user_data;
    }
    throw new Error("Unexpected API response structure");
  };

  const sendCode = async (isResend = false) => {
    const err = validateEmail(email);
    setEmailError(err);
    if (err) return;

    setIsSending(true);
    try {
      await requestOtp(email);
      setStep("code");
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setCodeError(null);
      toast({
        title: intl.formatMessage({ id: "OtpSentTitle" }),
        description: intl.formatMessage({ id: "OtpSentDescription" }),
        status: "success",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      const status = error?.response?.status;
      const description =
        status === 429
          ? intl.formatMessage({ id: "OtpRateLimited" })
          : intl.formatMessage({ id: "OtpSendFailed" });
      toast({
        title: intl.formatMessage({ id: "OtpSendFailedTitle" }),
        description,
        status: "error",
        position: "top",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const completeLogin = async (apiKey: string, message?: string) => {
    trackUserLogin("email");
    // Any captured referral id has now been consumed by this sign-in (the
    // backend applied it only if a new account was created).
    localStorage.removeItem(REF_STORAGE_KEY);
    storeUserApiKey(dispatch, apiKey);
    // Drop any leftover Google identity and mark this as an email session so a
    // stale google_data/auth_type pair can't resurrect the old Google profile.
    clearGoogleIdentity();
    localStorage.setItem("auth_type", AuthType.email);

    try {
      const userProfile = await getUserProfile(apiKey);
      dispatch(setIsUserLoggedIn(true));
      dispatch(setUserEmail(userProfile.email));
      dispatch(setUserId(userProfile.user_id));
      dispatch(setUserNickname(userProfile.username));
      dispatch(setUserRegDate(userProfile.reg_date));
      localStorage.setItem(
        "user_data",
        JSON.stringify({
          email: userProfile.email,
          userId: userProfile.user_id,
          userNickname: userProfile.username,
          regDate: userProfile.reg_date,
        })
      );
    } catch {
      // Fallback: use the entered email if the profile fetch fails.
      dispatch(setIsUserLoggedIn(true));
      dispatch(setUserEmail(email));
      dispatch(setUserId(Date.now()));
      dispatch(setUserNickname(email.split("@")[0]));
      dispatch(setUserRegDate(new Date().toISOString().split("T")[0]));
      localStorage.setItem(
        "user_data",
        JSON.stringify({
          email,
          userId: Date.now(),
          userNickname: email.split("@")[0],
          regDate: new Date().toISOString().split("T")[0],
        })
      );
    }

    toast({
      title: intl.formatMessage({ id: "LoginSuccessTitleText" }),
      description:
        message || intl.formatMessage({ id: "LoginSuccessDescriptionText" }),
      status: "success",
      position: "top",
      duration: 3000,
      isClosable: true,
    });
    returnAfterSignIn();
  };

  const submitCode = async () => {
    if (!code) {
      setCodeError("Code is required");
      return;
    }
    setIsVerifying(true);
    try {
      const data = await verifyOtp(email, code);
      if (data.status === "ok" && data.api_key) {
        setCodeError(null);
        await completeLogin(data.api_key, data.message);
      } else {
        setCodeError(intl.formatMessage({ id: "OtpInvalidCode" }));
      }
    } catch (error: any) {
      const status = error?.response?.status;
      setCodeError(
        status === 429
          ? intl.formatMessage({ id: "OtpRateLimited" })
          : intl.formatMessage({ id: "OtpInvalidCode" })
      );
    } finally {
      setIsVerifying(false);
    }
  };

  if (step === "code") {
    return (
      <form
        id="otp-code-form"
        onSubmit={(e) => {
          e.preventDefault();
          submitCode();
        }}
      >
        <Text mb={4}>
          <FormattedMessage id="OtpEnterCodeFor" /> <b>{email}</b>
        </Text>
        <FormControl isInvalid={!!codeError} className="login-form-input">
          <FormLabel>
            <FormattedMessage id="OtpCodeLabel" />
          </FormLabel>
          <Input
            name="code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="000000"
            inputMode="numeric"
            autoComplete="one-time-code"
          />
          <FormErrorMessage>{codeError}</FormErrorMessage>
        </FormControl>
        <Button
          mt={5}
          isLoading={isVerifying}
          type="submit"
          colorScheme="blue"
          width="100%"
        >
          <FormattedMessage id="OtpVerifyButton" />
        </Button>
        <Button
          mt={2}
          variant="ghost"
          width="100%"
          isDisabled={cooldown > 0 || isSending}
          isLoading={isSending}
          onClick={() => sendCode(true)}
        >
          {cooldown > 0 ? (
            <FormattedMessage
              id="OtpResendIn"
              values={{ seconds: cooldown }}
            />
          ) : (
            <FormattedMessage id="OtpResendButton" />
          )}
        </Button>
        <Button
          mt={2}
          variant="link"
          width="100%"
          onClick={() => {
            setStep("email");
            setCode("");
            setCodeError(null);
          }}
        >
          <FormattedMessage id="OtpChangeEmail" />
        </Button>
      </form>
    );
  }

  return (
    <form
      id="otp-email-form"
      onSubmit={(e) => {
        e.preventDefault();
        sendCode();
      }}
    >
      <FormControl isInvalid={!!emailError} className="login-form-input">
        <FormLabel>Email</FormLabel>
        <Input
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
        />
        <FormErrorMessage>{emailError}</FormErrorMessage>
      </FormControl>
      <Button
        mt={5}
        isLoading={isSending}
        type="submit"
        colorScheme="blue"
        width="100%"
      >
        <FormattedMessage id="OtpSendCodeButton" />
      </Button>
    </form>
  );
};

export default LoginForm;
